import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { path: '/', label: '首页', icon: 'ri-home-4-line' },
  { path: '/category/love', label: '恋爱', icon: 'ri-heart-line' },
  { path: '/category/fantasy', label: '奇幻', icon: 'ri-magic-line' },
  { path: '/category/action', label: '动作', icon: 'ri-flashlight-line' },
  { path: '/category/thriller', label: '悬疑', icon: 'ri-search-eye-line' },
  { path: '/about', label: '关于我们', icon: 'ri-information-line' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    if (mobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-book-open-line text-lg text-white"></i>
          </div>
          <span className={`font-heading text-xl font-bold tracking-tight ${scrolled ? 'text-foreground-900' : 'text-white'}`}>
            韩漫网
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? scrolled
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-white/20 text-white'
                  : scrolled
                    ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-600 hover:bg-background-200/70' : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
          <Link
            to="/about"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'bg-primary-500 text-white hover:bg-primary-600'
                : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
            }`}
          >
            开始阅读
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer ${
            scrolled ? 'text-foreground-700' : 'text-white'
          }`}
          aria-label="菜单"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-3">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === link.path
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              <i className={`${link.icon} text-base`}></i>
              {link.label}
            </Link>
          ))}
          <div className="mt-3 pt-3 border-t border-background-200/60">
            <Link
              to="/about"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold w-full whitespace-nowrap cursor-pointer"
            >
              开始阅读
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}